import React, { useEffect, useState, useRef } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Select, Space, Button, Typography } from 'antd';
import { ReloadOutlined, ClockCircleOutlined, TrophyOutlined } from '@ant-design/icons';
import { memoAPI } from '../services/api';

const { Title, Text } = Typography;

const { Option } = Select;

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalCount: 0,
    monthlyCount: 0,
    groupStats: {},
    remarkStats: {}
  });
  const [loading, setLoading] = useState(true);
  const [groupSortType, setGroupSortType] = useState('total'); // 'total', 'custom'
  const [remarkSortType, setRemarkSortType] = useState('total'); // 'total', 'custom'
  const [groupPageSize, setGroupPageSize] = useState(10);
  const [remarkPageSize, setRemarkPageSize] = useState(10);

  const [serverTime, setServerTime] = useState(new Date());

  const [rankings, setRankings] = useState({
    today: { remarks: [] },
    week: { remarks: [] },
    month: { remarks: [] }
  });

  // 重试计数器
  const [retryCount, setRetryCount] = useState(0);
  const [rankingsRetryCount, setRankingsRetryCount] = useState(0);
  const [timeRetryCount, setTimeRetryCount] = useState(0);

  // 请求状态标记，防止重复请求
  const isFetchingStats = useRef(false);
  const isFetchingRankings = useRef(false);
  const isFetchingTime = useRef(false);

  useEffect(() => {
    fetchStats();
    fetchRankings();
    fetchServerTime();
    
    // 设置时间更新定时器，每秒更新一次
    const timeInterval = setInterval(() => {
      setServerTime(new Date());
    }, 1000);

    // 清理定时器
    return () => clearInterval(timeInterval);
  }, []); // 移除 timeOffset 依赖

  const fetchStats = async () => {
    // 防止重复请求
    if (isFetchingStats.current) {
      return;
    }
    
    try {
      isFetchingStats.current = true;
      setLoading(true);
      const res = await memoAPI.getStats();
      setStats(res.data);
      setRetryCount(0); // 成功后重置重试计数
    } catch (error) {
      console.error('获取统计信息失败:', error);
      // 限制重试次数，最多重试3次
      if (retryCount < 3) {
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          console.log(`请求超时，正在重试... (${retryCount + 1}/3)`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            fetchStats();
          }, 3000);
        } else if (error.response?.status === 503) {
          console.log(`数据库连接超时，正在重试... (${retryCount + 1}/3)`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            fetchStats();
          }, 5000);
        }
      } else {
        console.error('重试次数已达上限，停止重试');
        setStats({
          totalCount: 0,
          finishedCount: 0,
          unfinishedCount: 0,
          todayCount: 0,
          monthlyCount: 0,
          groupStats: {},
          remarkStats: {}
        });
      }
    } finally {
      setLoading(false);
      isFetchingStats.current = false;
    }
  };

  const fetchRankings = async () => {
    // 防止重复请求
    if (isFetchingRankings.current) {
      return;
    }
    
    try {
      isFetchingRankings.current = true;
      const res = await memoAPI.getRankings();
      setRankings(res.data);
      setRankingsRetryCount(0); // 成功后重置重试计数
    } catch (error) {
      console.error('获取排行榜失败:', error);
      // 限制重试次数，最多重试3次
      if (rankingsRetryCount < 3) {
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          console.log(`排行榜请求超时，正在重试... (${rankingsRetryCount + 1}/3)`);
          setRankingsRetryCount(prev => prev + 1);
          setTimeout(() => {
            fetchRankings();
          }, 2000);
        }
      } else {
        console.error('排行榜重试次数已达上限，停止重试');
        setRankings({
          today: { remarks: [] },
          week: { remarks: [] },
          month: { remarks: [] }
        });
      }
    } finally {
      isFetchingRankings.current = false;
    }
  };

  const fetchServerTime = async () => {
    // 防止重复请求
    if (isFetchingTime.current) {
      return;
    }
    
    try {
      isFetchingTime.current = true;
      const res = await memoAPI.getServerTime();
      setServerTime(new Date(res.data.serverTime));
      setTimeRetryCount(0); // 成功后重置重试计数
    } catch (error) {
      console.error('获取服务器时间失败:', error);
      // 如果获取服务器时间失败，使用本地时间
      setServerTime(new Date());
      // 限制重试次数，最多重试3次
      if (timeRetryCount < 3) {
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          console.log(`服务器时间请求超时，正在重试... (${timeRetryCount + 1}/3)`);
          setTimeRetryCount(prev => prev + 1);
          setTimeout(() => {
            fetchServerTime();
          }, 2000);
        }
      } else {
        console.error('服务器时间重试次数已达上限，停止重试');
      }
    } finally {
      isFetchingTime.current = false;
    }
  };

  // 格式化时间显示
  const formatTime = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // 获取星期几
  const getWeekday = (date) => {
    const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    return weekdays[date.getDay()];
  };

  // 处理组别数据，支持排序
  const getGroupData = () => {
    const groupStats = stats.groupStats || {};
    const data = Object.entries(groupStats).map(([group, data], index) => ({
      key: `group-${group}-${index}`,
      name: group,
      今日: data.today || 0,
      昨日: data.yesterday || 0,
      前日: data.dayBeforeYesterday || 0,
      总计: data.total || 0  // 使用后端返回的总数量
    }));

    if (groupSortType === 'total') {
      return data.sort((a, b) => b.总计 - a.总计);
    } else {
      return data.sort((a, b) => a.name.localeCompare(b.name));
    }
  };

  // 处理备注数据，支持排序
  const getRemarkData = () => {
    const remarkStats = stats.remarkStats || {};
    
    const data = Object.entries(remarkStats).map(([remark, data], index) => ({
      key: `remark-${remark}-${index}`,
      name: remark,
      今日: data.today || 0,
      昨日: data.yesterday || 0,
      前日: data.dayBeforeYesterday || 0,
      总计: data.total || 0  // 使用后端返回的总数量
    }));

    if (remarkSortType === 'total') {
      return data.sort((a, b) => b.总计 - a.总计);
    } else {
      return data.sort((a, b) => a.name.localeCompare(b.name));
    }
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '今日',
      dataIndex: '今日',
      key: 'today',
      render: (value) => <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{value}</span>
    },
    {
      title: '昨日',
      dataIndex: '昨日',
      key: 'yesterday',
      render: (value) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{value}</span>
    },
    {
      title: '前日',
      dataIndex: '前日',
      key: 'dayBeforeYesterday',
      render: (value) => <span style={{ color: '#722ed1', fontWeight: 'bold' }}>{value}</span>
    },
    {
      title: '总计',
      dataIndex: '总计',
      key: 'total',
      render: (value) => <span style={{ color: '#fa8c16', fontWeight: 'bold' }}>{value}</span>
    }
  ];

  // 排行榜表格列定义
  const rankingColumns = [
    {
      title: '排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank) => {
        const colors = ['#FFD700', '#C0C0C0', '#CD7F32', '#1890ff', '#52c41a'];
        return (
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: rank <= 3 ? colors[rank - 1] : '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: rank <= 3 ? '#fff' : '#666',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            {rank}
          </div>
        );
      }
    },
    {
      title: '备注名称',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Tag color="purple">{text}</Tag>
    },
    {
      title: '数量',
      dataIndex: 'count',
      key: 'count',
      render: (value) => <span style={{ color: '#fa8c16', fontWeight: 'bold', fontSize: '16px' }}>{value}</span>
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 时间显示区域 */}
      <Row style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card 
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
              borderRadius: '16px'
            }}
            styles={{ body: { padding: '20px 32px' } }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  padding: '12px',
                  marginRight: '16px',
                  backdropFilter: 'blur(10px)'
                }}>
                  <ClockCircleOutlined style={{ fontSize: '28px' }} />
                </div>
                <div>
                  <Title level={3} style={{ color: 'white', margin: 0, fontWeight: 'bold' }}>
                    {formatTime(serverTime)}
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', fontWeight: '500' }}>
                    {getWeekday(serverTime)} (服务器时间)
                  </Text>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  backdropFilter: 'blur(10px)'
                }}>
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: '500' }}>
                    ⚡ 实时更新
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 第一行统计卡片 */}
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(63, 134, 0, 0.1)',
              border: '1px solid rgba(63, 134, 0, 0.1)'
            }}
            styles={{ body: { padding: '24px' } }}
          >
            <Statistic
              title={<span style={{ fontSize: '16px', fontWeight: '500', color: '#666' }}>总建站数量</span>}
              value={stats.totalCount || 0}
              valueStyle={{ color: '#3f8600', fontSize: '32px', fontWeight: 'bold' }}
              suffix={<span style={{ fontSize: '16px', color: '#3f8600' }}>个</span>}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(82, 196, 26, 0.1)',
              border: '1px solid rgba(82, 196, 26, 0.1)'
            }}
            styles={{ body: { padding: '24px' } }}
          >
            <Statistic
              title={<span style={{ fontSize: '16px', fontWeight: '500', color: '#666' }}>已完成建站</span>}
              value={stats.finishedCount || 0}
              valueStyle={{ color: '#52c41a', fontSize: '32px', fontWeight: 'bold' }}
              suffix={<span style={{ fontSize: '16px', color: '#52c41a' }}>个</span>}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(245, 34, 45, 0.1)',
              border: '1px solid rgba(245, 34, 45, 0.1)'
            }}
            styles={{ body: { padding: '24px' } }}
          >
            <Statistic
              title={<span style={{ fontSize: '16px', fontWeight: '500', color: '#666' }}>未完成建站</span>}
              value={stats.unfinishedCount || 0}
              valueStyle={{ color: '#f5222d', fontSize: '32px', fontWeight: 'bold' }}
              suffix={<span style={{ fontSize: '16px', color: '#f5222d' }}>个</span>}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(250, 84, 28, 0.1)',
              border: '1px solid rgba(250, 84, 28, 0.1)'
            }}
            styles={{ body: { padding: '24px' } }}
          >
            <Statistic
              title={<span style={{ fontSize: '16px', fontWeight: '500', color: '#666' }}>今日建站数量</span>}
              value={stats.todayCreatedCount || 0}
              valueStyle={{ color: '#fa541c', fontSize: '32px', fontWeight: 'bold' }}
              suffix={<span style={{ fontSize: '16px', color: '#fa541c' }}>个</span>}
            />
          </Card>
        </Col>
      </Row>

      {/* 第二行统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={6}>
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(24, 144, 255, 0.1)',
              border: '1px solid rgba(24, 144, 255, 0.1)'
            }}
            styles={{ body: { padding: '24px' } }}
          >
            <Statistic
              title={<span style={{ fontSize: '16px', fontWeight: '500', color: '#666' }}>当月建站数量</span>}
              value={stats.monthlyCount || 0}
              valueStyle={{ color: '#1890ff', fontSize: '32px', fontWeight: 'bold' }}
              suffix={<span style={{ fontSize: '16px', color: '#1890ff' }}>个</span>}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(114, 46, 209, 0.1)',
              border: '1px solid rgba(114, 46, 209, 0.1)'
            }}
            styles={{ body: { padding: '24px' } }}
          >
            <Statistic
              title={<span style={{ fontSize: '16px', fontWeight: '500', color: '#666' }}>活跃组别</span>}
              value={Object.keys(stats.groupStats || {}).length}
              valueStyle={{ color: '#722ed1', fontSize: '32px', fontWeight: 'bold' }}
              suffix={<span style={{ fontSize: '16px', color: '#722ed1' }}>个</span>}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card 
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(250, 140, 22, 0.1)',
              border: '1px solid rgba(250, 140, 22, 0.1)'
            }}
            styles={{ body: { padding: '24px' } }}
          >
            <Statistic
              title={<span style={{ fontSize: '16px', fontWeight: '500', color: '#666' }}>备注分类</span>}
              value={Object.keys(stats.remarkStats || {}).length}
              valueStyle={{ color: '#fa8c16', fontSize: '32px', fontWeight: 'bold' }}
              suffix={<span style={{ fontSize: '16px', color: '#fa8c16' }}>个</span>}
            />
          </Card>
        </Col>
      </Row>

      {/* Remark排行榜区域 */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col span={8}>
          <Card 
            title={
              <Space>
                <TrophyOutlined style={{ color: '#FFD700', fontSize: '18px' }} />
                <span style={{ color: '#FFD700', fontWeight: 'bold' }}>今日Remark排行</span>
              </Space>
            }
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(255, 215, 0, 0.15)',
              border: '2px solid rgba(255, 215, 0, 0.1)',
              background: 'linear-gradient(135deg, #fff, #fff8e1)'
            }}
            styles={{ body: { padding: '16px' } }}
          >
            <Table
              columns={rankingColumns}
              dataSource={rankings.today.remarks.map((item, index) => ({
                key: `today-${index}`,
                rank: index + 1,
                name: item.name,
                count: item.count
              }))}
              pagination={false}
              size="small"
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card 
            title={
              <Space>
                <TrophyOutlined style={{ color: '#C0C0C0', fontSize: '18px' }} />
                <span style={{ color: '#C0C0C0', fontWeight: 'bold' }}>本周Remark排行</span>
              </Space>
            }
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(192, 192, 192, 0.15)',
              border: '2px solid rgba(192, 192, 192, 0.1)',
              background: 'linear-gradient(135deg, #fff, #f8f9fa)'
            }}
            styles={{ body: { padding: '16px' } }}
          >
            <Table
              columns={rankingColumns}
              dataSource={rankings.week.remarks.map((item, index) => ({
                key: `week-${index}`,
                rank: index + 1,
                name: item.name,
                count: item.count
              }))}
              pagination={false}
              size="small"
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card 
            title={
              <Space>
                <TrophyOutlined style={{ color: '#CD7F32', fontSize: '18px' }} />
                <span style={{ color: '#CD7F32', fontWeight: 'bold' }}>本月Remark排行</span>
              </Space>
            }
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(205, 127, 50, 0.15)',
              border: '2px solid rgba(205, 127, 50, 0.1)',
              background: 'linear-gradient(135deg, #fff, #fff8dc)'
            }}
            styles={{ body: { padding: '16px' } }}
          >
            <Table
              columns={rankingColumns}
              dataSource={rankings.month.remarks.map((item, index) => ({
                key: `month-${index}`,
                rank: index + 1,
                name: item.name,
                count: item.count
              }))}
              pagination={false}
              size="small"
              locale={{ emptyText: '暂无数据' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 详细统计表格 */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <span>组别建站统计</span>
                <Select 
                  value={groupSortType} 
                  onChange={setGroupSortType}
                  style={{ width: 120 }}
                  size="small"
                >
                  <Option value="total">按数量排序</Option>
                  <Option value="custom">按名称排序</Option>
                </Select>
                <Button 
                  icon={<ReloadOutlined />} 
                  size="small" 
                  onClick={() => {
                    setRetryCount(0);
                    fetchStats();
                  }}
                  disabled={isFetchingStats.current}
                />
              </Space>
            } 
            loading={loading}
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: 'none'
            }}
            styles={{ body: { padding: '24px' } }}
          >
            <Table
              columns={columns}
              dataSource={getGroupData()}
              pagination={{
                pageSize: groupPageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onShowSizeChange: (current, size) => setGroupPageSize(size)
              }}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <span>备注分类统计</span>
                <Select 
                  value={remarkSortType} 
                  onChange={setRemarkSortType}
                  style={{ width: 120 }}
                  size="small"
                >
                  <Option value="total">按数量排序</Option>
                  <Option value="custom">按名称排序</Option>
                </Select>
                <Button 
                  icon={<ReloadOutlined />} 
                  size="small" 
                  onClick={() => {
                    setRetryCount(0);
                    fetchStats();
                  }}
                  disabled={isFetchingStats.current}
                />
              </Space>
            } 
            loading={loading}
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: 'none'
            }}
            styles={{ body: { padding: '24px' } }}
          >
            <Table
              columns={columns}
              dataSource={getRemarkData()}
              pagination={{
                pageSize: remarkPageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onShowSizeChange: (current, size) => setRemarkPageSize(size)
              }}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
} 